import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import attendanceService from "../../services/attendanceService";
import { queryKeys } from "./queryKeys";

// Fetch attendance list
export const useAttendance = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.attendance.list(filters),
    queryFn: () => attendanceService.getAttendance(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes for attendance data
  });
};

// Fetch single attendance record
export const useAttendanceById = (id) => {
  return useQuery({
    queryKey: queryKeys.attendance.detail(id),
    queryFn: () => attendanceService.getAttendanceById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

// Get student attendance
export const useStudentAttendance = (studentId, filters = {}) => {
  return useQuery({
    queryKey: queryKeys.attendance.student(studentId, filters),
    queryFn: () => attendanceService.getStudentAttendance(studentId, filters),
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  });
};

// Get class attendance
export const useClassAttendance = (classId, date) => {
  return useQuery({
    queryKey: queryKeys.attendance.class(classId, date),
    queryFn: () => attendanceService.getClassAttendance(classId, { date }),
    enabled: !!classId && !!date,
    staleTime: 1 * 60 * 1000, // 1 minute for class attendance
  });
};

// Get attendance by date
export const useAttendanceByDate = (date, classId) => {
  return useQuery({
    queryKey: queryKeys.attendance.class(classId, date),
    queryFn: () => attendanceService.getAttendanceByDate(date, classId),
    enabled: !!date && !!classId,
    staleTime: 1 * 60 * 1000,
  });
};

// Get active session
export const useActiveSession = (classId) => {
  return useQuery({
    queryKey: [...queryKeys.attendance.all, "activeSession", classId],
    queryFn: () => attendanceService.getActiveSession(classId),
    enabled: !!classId,
    refetchInterval: 30000, // Refetch every 30 seconds for active session
    staleTime: 0, // Always consider stale for active session
  });
};

// Get attendance statistics
export const useAttendanceStatistics = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.attendance.all, "statistics", filters],
    queryFn: () => attendanceService.getStatistics(filters),
    staleTime: 5 * 60 * 1000,
  });
};

// Get attendance report
export const useAttendanceReport = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.attendance.all, "report", filters],
    queryFn: () => attendanceService.getAttendanceReport(filters),
    staleTime: 5 * 60 * 1000,
  });
};

// Start attendance session
export const useStartAttendanceSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionData) => attendanceService.startSession(sessionData),
    onSuccess: (data, variables) => {
      // Invalidate active session query
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.attendance.all, "activeSession"],
      });
      // Invalidate class attendance
      if (variables.classId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.attendance.class(variables.classId),
        });
      }
    },
  });
};

// End attendance session
export const useEndAttendanceSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId) => attendanceService.endSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.attendance.all, "activeSession"],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.attendance.lists(),
      });
    },
  });
};

// Mark attendance
export const useMarkAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attendanceData) =>
      attendanceService.markAttendance(attendanceData),
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.attendance.all,
      });
      if (variables.studentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.attendance.student(variables.studentId),
        });
      }
      if (variables.classId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.attendance.class(variables.classId),
        });
      }
    },
  });
};

// Mark bulk attendance
export const useMarkBulkAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attendanceData) =>
      attendanceService.markBulkAttendance(attendanceData),
    onSuccess: (data, variables) => {
      // Invalidate all attendance queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.attendance.all,
      });
      if (variables.classId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.attendance.class(variables.classId),
        });
      }
    },
  });
};

// Update attendance
export const useUpdateAttendance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, attendanceData }) =>
      attendanceService.updateAttendance(id, attendanceData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.attendance.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.attendance.lists(),
      });
    },
  });
};

// Download attendance report
export const useDownloadAttendanceReport = () => {
  return useMutation({
    mutationFn: (params) => attendanceService.downloadReport(params),
  });
};

